import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Doctor, Service, Patient, Appointment, User, ClinicInfo, DashboardStats } from '../types';

interface AppState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;
  
  // Data
  doctors: Doctor[];
  services: Service[];
  patients: Patient[];
  appointments: Appointment[];
  clinicInfo: ClinicInfo | null;
  dashboardStats: DashboardStats | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  sidebarOpen: boolean;
}

type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_DOCTORS'; payload: Doctor[] }
  | { type: 'SET_SERVICES'; payload: Service[] }
  | { type: 'SET_PATIENTS'; payload: Patient[] }
  | { type: 'SET_APPOINTMENTS'; payload: Appointment[] }
  | { type: 'ADD_APPOINTMENT'; payload: Appointment }
  | { type: 'UPDATE_APPOINTMENT'; payload: Appointment }
  | { type: 'DELETE_APPOINTMENT'; payload: string }
  | { type: 'SET_CLINIC_INFO'; payload: ClinicInfo }
  | { type: 'SET_DASHBOARD_STATS'; payload: DashboardStats }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'LOGIN'; payload: User }
  | { type: 'LOGOUT' };

const initialState: AppState = {
  user: null,
  isAuthenticated: false,
  doctors: [],
  services: [],
  patients: [],
  appointments: [],
  clinicInfo: null,
  dashboardStats: null,
  loading: false,
  error: null,
  sidebarOpen: false,
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_USER':
      return { ...state, user: action.payload, isAuthenticated: !!action.payload };
    
    case 'LOGIN':
      return { ...state, user: action.payload, isAuthenticated: true };
    
    case 'LOGOUT':
      return { ...state, user: null, isAuthenticated: false };
    
    case 'SET_DOCTORS':
      return { ...state, doctors: action.payload };
    
    case 'SET_SERVICES':
      return { ...state, services: action.payload };
    
    case 'SET_PATIENTS':
      return { ...state, patients: action.payload };
    
    case 'SET_APPOINTMENTS':
      return { ...state, appointments: action.payload };
    
    case 'ADD_APPOINTMENT':
      return { ...state, appointments: [...state.appointments, action.payload] };
    
    case 'UPDATE_APPOINTMENT':
      return {
        ...state,
        appointments: state.appointments.map(apt =>
          apt.id === action.payload.id ? action.payload : apt
        ),
      };
    
    case 'DELETE_APPOINTMENT':
      return {
        ...state,
        appointments: state.appointments.filter(apt => apt.id !== action.payload),
      };
    
    case 'SET_CLINIC_INFO':
      return { ...state, clinicInfo: action.payload };
    
    case 'SET_DASHBOARD_STATS':
      return { ...state, dashboardStats: action.payload };
    
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarOpen: !state.sidebarOpen };
    
    default:
      return state;
  }
};

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Custom hooks for specific data
export const useAuth = () => {
  const { state, dispatch } = useAppContext();
  
  const login = (user: User) => {
    // NO localStorage! Auth handled by AuthContext + API
    dispatch({ type: 'LOGIN', payload: user });
  };
  
  const logout = () => {
    // NO localStorage! Auth handled by AuthContext + API
    dispatch({ type: 'LOGOUT' });
  };
  
  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    login,
    logout,
  };
};

export const useDoctors = () => {
  const { state, dispatch } = useAppContext();
  
  return {
    doctors: state.doctors,
    setDoctors: (doctors: Doctor[]) => dispatch({ type: 'SET_DOCTORS', payload: doctors }),
  };
};

export const useServices = () => {
  const { state, dispatch } = useAppContext();
  
  return {
    services: state.services,
    setServices: (services: Service[]) => dispatch({ type: 'SET_SERVICES', payload: services }),
  };
};

export const useAppointments = () => {
  const { state, dispatch } = useAppContext();
  
  return {
    appointments: state.appointments,
    setAppointments: (appointments: Appointment[]) => 
      dispatch({ type: 'SET_APPOINTMENTS', payload: appointments }),
    addAppointment: (appointment: Appointment) => 
      dispatch({ type: 'ADD_APPOINTMENT', payload: appointment }),
    updateAppointment: (appointment: Appointment) => 
      dispatch({ type: 'UPDATE_APPOINTMENT', payload: appointment }),
    deleteAppointment: (id: string) => 
      dispatch({ type: 'DELETE_APPOINTMENT', payload: id }),
  };
};